/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import type { User } from "@/features/auth/store/authStore"
import { analytics } from "@/lib/analytics"
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from "@/lib/analytics/constants"
import { apiClient } from "@/lib/api-client"
import { clearTokens, setTokens } from "@/lib/token-manager"
import { type BackendUser, transformUserResponse } from "@/lib/user-transformer"
import type {
  LoginRequest,
  ResendVerificationRequest,
  SignupRequest,
  VerifyEmailRequest,
} from "../types/auth.types"

// Transformed auth response with clean User object
export interface AuthResponse {
  user: User
  message: string
}

// Export types for convenience
export type { LoginRequest, ResendVerificationRequest, SignupRequest, VerifyEmailRequest }

/**
 * Register a new user
 * Sends profile data and returns tokens on success
 */
export async function signup(data: SignupRequest): Promise<AuthResponse> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      user: BackendUser
      tokens: { accessToken: string; refreshToken: string; expiresIn: number }
      deviceId: string
    }
    message: string
  }>("/auth/register", data)

  // Store tokens — user is fully activated at registration
  if (response.data.success) {
    setTokens({
      accessToken: response.data.data.tokens.accessToken,
      refreshToken: response.data.data.tokens.refreshToken,
      deviceId: response.data.data.deviceId,
    })
  }

  const user = transformUserResponse(response.data.data.user)

  analytics.track(ANALYTICS_EVENTS.SIGNUP_SUCCESS, {
    [ANALYTICS_PROPERTIES.EMAIL]: data.email,
    [ANALYTICS_PROPERTIES.SOURCE]: "email",
  })

  return { user, message: response.data.message }
}

/**
 * Login user
 */
export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      user: BackendUser
      tokens?: { accessToken: string; refreshToken: string; expiresIn: number }
      deviceId?: string
    }
    message: string
  }>("/auth/login", data)

  // Transform user response to remove Date objects
  const user = transformUserResponse(response.data.data.user)

  // Store tokens (even for unverified users so they can verify their email)
  if (response.data.success && response.data.data.tokens) {
    setTokens({
      accessToken: response.data.data.tokens.accessToken,
      refreshToken: response.data.data.tokens.refreshToken,
      deviceId: response.data.data.deviceId || "",
    })
  }

  // Track login event
  analytics.track(ANALYTICS_EVENTS.LOGIN_SUCCESS, { [ANALYTICS_PROPERTIES.METHOD]: "email" })

  // Identify user in analytics
  analytics.identify(user.id, {
    $email: user.email,
    name: user.name,
    username: user.username,
  })

  return { user, message: response.data.message }
}

/**
 * Verify email with OTP (protected endpoint - user from auth token)
 */
export async function verifyEmail(data: VerifyEmailRequest): Promise<AuthResponse> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      user: BackendUser
    }
    message: string
  }>("/api/verify-email", data)

  const user = transformUserResponse(response.data.data.user)

  analytics.track(ANALYTICS_EVENTS.EMAIL_VERIFIED)

  analytics.identify(user.id, {
    $email: user.email,
    name: user.name,
    username: user.username,
  })

  return { user, message: response.data.message }
}

/**
 * Verify email via magic link (email & token in URL query params)
 */
export async function verifyEmailByLink(email: string, token: string): Promise<AuthResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: {
      user: BackendUser
    }
    message: string
  }>("/auth/verify-email-link", {
    params: { email, token },
  })

  const user = transformUserResponse(response.data.data.user)

  analytics.track(ANALYTICS_EVENTS.EMAIL_VERIFIED)

  analytics.identify(user.id, {
    $email: user.email,
    name: user.name,
    username: user.username,
  })

  return { user, message: response.data.message }
}

/**
 * Resend verification email
 */
export async function resendVerification(
  data: ResendVerificationRequest,
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>("/auth/resend-verification", data)
  return response.data
}

/**
 * Logout from current device
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post("/api/logout")
  } finally {
    // Always clear tokens, even if API call fails
    clearTokens()
    // Track logout event
    analytics.track(ANALYTICS_EVENTS.LOGOUT)
    analytics.reset()
  }
}

/**
 * Logout from all devices
 */
export async function logoutAll(): Promise<void> {
  try {
    await apiClient.post("/api/logout-all")
  } finally {
    // Always clear tokens, even if API call fails
    clearTokens()
    // Track logout event
    analytics.track(ANALYTICS_EVENTS.LOGOUT)
    analytics.reset()
  }
}

/**
 * Refresh access token
 */
export async function refreshToken(refreshToken: string, deviceId: string): Promise<AuthResponse> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      user: BackendUser
      tokens: { accessToken: string; refreshToken: string; expiresIn: number }
      deviceId: string
    }
    message: string
  }>("/api/refresh-token", {
    refreshToken,
    deviceId,
  })

  // Update stored tokens
  if (response.data.success) {
    setTokens({
      accessToken: response.data.data.tokens.accessToken,
      refreshToken: response.data.data.tokens.refreshToken,
      deviceId: response.data.data.deviceId,
    })
  }

  // Transform user response to remove Date objects
  const user = transformUserResponse(response.data.data.user)

  return { user, message: response.data.message }
}

/**
 * Request password reset
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    "/auth/forgot-password",
    {
      email,
    },
  )
  return { message: response.data.message }
}

/**
 * Reset password with token
 */
export async function resetPassword(
  email: string,
  token: string,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    "/auth/reset-password",
    {
      email,
      token,
      newPassword,
    },
  )

  // Track password reset event
  analytics.track(ANALYTICS_EVENTS.PASSWORD_RESET)

  return { message: response.data.message }
}
